import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import i18n from "i18next";
import { DocumentConnectionView } from "src/api/generated/postgres/db/types/documents/tables/documentConnectionView.ts";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import Typography from "@mui/material/Typography";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { useMessageDialog } from "src/components/common/dialogs/messageDialog/MessageDialogContext.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { DocumentPreviewView } from "src/components/views/documents/preview/DocumentPreviewView.tsx";
import { faArrowDown, faArrowUp, faExternalLink } from "@fortawesome/pro-regular-svg-icons";
import { useFieldArray } from "react-hook-form";
import { AavoCheckbox } from "src/components/common/inputFields/AavoCheckbox.tsx";
import {
	CustomerOfferContractSigningApi,
	CustomerOfferContractSigningApi_InitData,
} from "src/api/generated/erp/sales/customerOrderOffer/contracts/api/customerOfferContractSigningApi.ts";
import { filterNulls } from "../../../../../utils/arrayUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SendCustomerOfferContractForSigningFormProps extends FormCommonProps<void> {
	customerOfferId: number;
}

interface FormValues {
	documents: DocumentFormValues[];
}

interface DocumentFormValues extends DocumentConnectionView {
	attach: boolean;
}

export const SendCustomerOfferContractForSigningForm = ({
	customerOfferId,
	onFormEdited,
	onCompleted,
}: SendCustomerOfferContractForSigningFormProps) => {
	const showMessageDialog = useMessageDialog();

	return (
		<AsyncForm<CustomerOfferContractSigningApi_InitData, FormValues, void>
			fetch={() =>
				CustomerOfferContractSigningApi.getInitData({
					customerOfferId,
				})
			}
			getDefaultValues={(initData) => ({
				documents: initData.documents.map((document) => ({
					...document,
					attach: document.isPinned,
				})),
			})}
			columns={1}
			noLayout
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.customerOfferContractSigning.form"),
				data: {
					customerOfferId,
				},
			}}
			render={(params) => <FormContent {...params} customerOfferId={customerOfferId} />}
			submitLabel={i18n.t("send")}
			submit={async ({ documents }, { data }) => {
				if (data.customerContacts.length === 0) {
					showMessageDialog({
						content: i18n.t("contract_signing_missing_customer_contacts"),
					});
					return "interrupted";
				}
				if (data.salesPerson == null) {
					showMessageDialog({
						content: i18n.t("contract_signing_missing_sales_person"),
					});
					return "interrupted";
				}

				await CustomerOfferContractSigningApi.beginContractSigningProcess({
					customerOfferId,
					documentIdsToAttach: documents.filter((doc) => doc.attach).map((doc) => doc.documentId),
				});
			}}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
		/>
	);
};

interface FormContentProps extends AsyncFormContentParams<CustomerOfferContractSigningApi_InitData, FormValues> {
	customerOfferId: number;
}

const FormContent = ({ control, customerOfferId, data: { salesPerson, customerContacts } }: FormContentProps) => {
	const { openDialog } = useGenericDialog();

	const documentsFieldArray = useFieldArray({
		control,
		name: "documents",
	});
	const documentCount = documentsFieldArray.fields.length;

	const receivers: { name: string; email: string }[] = filterNulls([
		salesPerson == null ? null : { name: salesPerson.name, email: salesPerson.email },
		...customerContacts.map((contact) => ({
			name: contact.name,
			email: contact.email,
		})),
	]);

	return (
		<VerticalBox
			sx={{
				overflowY: "auto",
				margin: 1,
				marginX: 2,
			}}
		>
			<Typography
				variant={"h6"}
				sx={{
					marginBottom: 1,
				}}
			>
				{i18n.t("receivers")}
			</Typography>
			<VerticalBox
				sx={{
					marginBottom: 2,
					gap: 0.5,
				}}
			>
				{receivers.map(({ name, email }, index) => (
					<Typography key={index} variant={"body2"}>
						{`${name} - ${email}`}
					</Typography>
				))}
			</VerticalBox>
			<Typography
				variant={"h6"}
				sx={{
					marginBottom: 1,
				}}
			>
				{i18n.t("select_documents_to_attach")}
			</Typography>
			<AavoCheckbox disabled checked label={i18n.t("contract_printout")} />
			{documentsFieldArray.fields.map((doc, idx) => (
				<HorizontalBox
					key={doc.id}
					sx={{
						alignItems: "center",
					}}
				>
					<FormCheckbox
						control={control}
						name={`documents.${idx}.attach`}
						label={doc.description1}
						sx={{
							marginRight: 1,
						}}
					/>
					<AsyncButton
						icon={faExternalLink}
						tooltip={i18n.t("open")}
						sx={{
							fontSize: "1rem",
						}}
						onClick={() => {
							openDialog(() => ({
								title: doc.description1,
								agentContext: {
									key: defineAgentUiContextKey("erp.sales.customerOfferContractDocumentPreview.dialog"),
									pathSegments: [doc.description1],
									data: {
										customerOfferId,
										documentId: doc.documentId,
									},
								},
								content: <DocumentPreviewView documentId={doc.documentId} />,
							}));
						}}
					/>
					<AsyncButton
						icon={faArrowUp}
						tooltip={i18n.t("move_up")}
						disabled={idx === 0}
						size={"small"}
						onClick={() => documentsFieldArray.move(idx, idx - 1)}
					/>
					<AsyncButton
						icon={faArrowDown}
						tooltip={i18n.t("move_down")}
						disabled={idx === documentCount - 1}
						size={"small"}
						onClick={() => documentsFieldArray.move(idx, idx + 1)}
					/>
				</HorizontalBox>
			))}
		</VerticalBox>
	);
};
